/* eslint-disable @typescript-eslint/naming-convention */
import { AfterViewInit, Component, ElementRef, EventEmitter, inject, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Payment } from '@models/account.model';
import { AddCardDialogComponent } from '@modules/setup/pages/payment-methods/add-card-dialog/add-card-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { AlertService } from '@services/alert.service';
import { ConfigLocaleService } from '@services/configLocale.service';
import { PaymentCard, PeachpayService } from '@services/peachpay.service';
import { UserService } from '@services/user.service';
import { downloadSummaryOfPayments } from '@utilities/pdf-utils';
import { DateTime } from 'luxon';
import { Subject, takeUntil } from 'rxjs';

// Fake it till you make it, or till the script is loaded
declare global {
  interface Window {
    PeachPayments: any; // Declare the PeachPayments property
    Checkout?: any; // Declare the Checkout property
  }
}

@Component({
  selector: 'gc-peachpay-embedding',
  templateUrl: './peachpay-embeddeding.component.html',
  styleUrls: ['./peachpay-embeddeding.component.scss'],
  standalone: false,
})
export class PeachpayEmbeddingComponent implements AfterViewInit, OnDestroy {
  @Input() payment: Payment | undefined;
  @Input() dialogRef: MatDialogRef<AddCardDialogComponent> | undefined;
  @Output() paymentCompleted: EventEmitter<'completed' | 'cancelled' | 'expired' | 'error' | ''> = new EventEmitter();
  @ViewChild('peachPaymentContainer', { static: false }) peachPaymentContainer: ElementRef | undefined;
  result: 'completed' | 'cancelled' | 'expired' | 'error' | '' = '';
  timestamp: DateTime;
  currency: string;
  unsubscribe$ = new Subject<void>();
  addCard: boolean = false;
  private readonly peachpayService: PeachpayService = inject(PeachpayService);
  private readonly alertService: AlertService = inject(AlertService);
  private readonly translateService: TranslateService = inject(TranslateService);
  private readonly userService: UserService = inject(UserService);
  private readonly configService: ConfigLocaleService = inject(ConfigLocaleService);

  ngAfterViewInit() {
    this.loadPeachPayScript();
    this.currency = this.configService.$currencyCode();
    this.timestamp = this.payment?.paidDate;
  }

  loadPeachPayScript() {
    this.result = '';
    const paymentHash = this.payment?.hash || null;
    this.peachpayService.getEmbeddedUrl$(paymentHash, this.userService.$userID()).subscribe((embedJson) => {
      const scriptElement = document.createElement('script');
      scriptElement.src = embedJson.checkout_url;
      if (embedJson.completed) {
        this.result = 'completed';
        this.closeDialog();
        return;
      } else if (!embedJson.checkout_url || !embedJson.entity_id || !embedJson.checkout_id) {
        console.error('Error loading PeachPay data:', this.payment, embedJson);
        this.result = 'error';
        this.closeDialog();
        return;
      }
      document.body.appendChild(scriptElement);
      scriptElement.onload = () => {
        const options = {
          checkoutId: embedJson.checkout_id,
          key: embedJson.entity_id,
          events: {
            onCompleted: (event) => {
              if (event.registrationId) {
                this.postPeachCard({
                  card: event.card,
                  registrationId: event.registrationId,
                });
                this.addCard = !paymentHash ? true : false;
              }
              this.timestamp = DateTime.fromISO(event.timestamp);
              this.currency = event.currency;
              this.result = 'completed';
              this.payment = {
                ...this.payment,
                merchantTransactionId: event.merchantTransactionId,
                paidDate: this.timestamp,
                paymentBrand: event.paymentBrand,
                cardLast4Digits: event.card.last4Digits,
              };
              this.paymentCompleted.emit('completed');
              this.closeDialog();
            },
            onCancelled: () => {
              this.result = 'cancelled';
              this.closeDialog();
            },
            onExpired: () => {
              this.result = 'expired';
              this.closeDialog();
            },
            onError: (error) => {
              console.error('PeachPay error:', error);
              this.result = 'error';
              this.alertService.notifyError$(this.translateService.instant('error_peachpay'));
              this.closeDialog();
            },
          },
          options: {
            theme: {
              brand: {
                primary: this.payment?.themeData?.theme_colours?.primary || '#595ca9',
              },
              cards: {
                backgroundHover: this.payment?.themeData?.theme_colours?.accent || '#bce132',
              },
            },
          },
          customisations: {
            card: {
              submitButtonText: this.payment?.hash ? 'Pay now' : 'Store card',
              showBillingFields: false,
            },
          },
        };

        const checkout = window.Checkout.initiate(options);
        checkout.render(this.peachPaymentContainer?.nativeElement);
      };
    });
  }

  onDownload() {
    downloadSummaryOfPayments(this.payment, this.translateService, this.timestamp);
  }

  postPeachCard(card: PaymentCard): void {
    this.peachpayService
      .postPaymentsCards$(this.userService.$userID(), card)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: () => {
          this.alertService.snackbar(this.translateService.instant('card_added_successfully'), this.translateService.instant('success'));
        },
        error: (error: unknown) => {
          console.error('Error posting card:', error);
          this.alertService.handleErrorDialog$(error, this.translateService.instant('error_adding_card'));
        },
      });
  }

  closeDialog(result?: any): void {
    if (this.dialogRef) {
      this.dialogRef.close(result);
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    const scriptElement = document.querySelector(`script[src*="peachpayments.com/checkout"]`);
    if (scriptElement && document.body.contains(scriptElement)) {
      document.body.removeChild(scriptElement);
    }
  }
}
